import { type HttpService } from '@storebrand-digital/bank-foundation-http-service';

import { type BankRestApiAccountsResponse, type BankAccountAgreement } from '../../app/types/bankAccountRestApi';
import { type Country } from '../../app/types/countries';
import {
  type Account,
  type AccountType,
  type Consents,
  type CustomerAccountCommand,
  type CustomerAccountProperties,
} from '../../app/types/createCustomer';
import { type Customer } from '../../app/types/customerCoreData';

export default class ApiService {
  #httpService: HttpService;

  constructor(httpService: HttpService) {
    this.#httpService = httpService;
  }

  getAccountsByType = async (accountType: AccountType) =>
    this.#httpService
      .get<Account[] | ''>(`bank/accounts/v2/account-type?accountType=${accountType}`)
      .then(({ data }) => data || []);

  getCountries = async () => (await this.#httpService.get<Country[]>('bank/loans/consumer-loans/countries')).data;

  getBankStatus = async () => (await this.#httpService.get<boolean>('bank/check/has-onboarded')).data;

  getBankAccountAgreement = async (bankAccountNumber: string) =>
    (
      await this.#httpService.get<BankRestApiAccountsResponse<BankAccountAgreement> | undefined>(
        `bank/accounts/${bankAccountNumber}`
      )
    ).data;

  createCustomerAccount = async (payload: CustomerAccountCommand) =>
    (
      await this.#httpService.post<CustomerAccountProperties, CustomerAccountCommand>(
        'me/customer-account/create',
        payload
      )
    ).data;

  getOngoingApplications = async () =>
    (await this.#httpService.get<CustomerAccountProperties[]>('/me/customer-account/applications/in-progress')).data;

  deleteOngoingApplication = async (id: string) =>
    (await this.#httpService.delete(`me/customer-account/applications/in-progress/${id}`)).data;

  // NOTE: Not sure if uid here is an application ID for account creation or other types (CLAP, LAP, etc)
  getCustomerAccount = async (uid: string) =>
    (await this.#httpService.get<CustomerAccountProperties>(`me/customer-account/${uid}`)).data;

  updateCustomerAccount = async (payload: CustomerAccountProperties) =>
    (
      await this.#httpService.patch<CustomerAccountProperties, CustomerAccountProperties>(
        'me/customer-account',
        payload
      )
    ).data;

  submitCustomerAccount = async (payload: CustomerAccountProperties) =>
    (
      await this.#httpService.post<CustomerAccountProperties, CustomerAccountProperties>(
        'me/customer-account/submit',
        payload
      )
    ).data;

  updateConsents = async (payload: Partial<Consents>) =>
    (await this.#httpService.patch('customer/core-data.v3', payload)).data;

  getCustomerCoreData = async () => (await this.#httpService.get<Customer>('customer/core-data.v3')).data;

  // TODO: Consider using Apollo client to generate correct typings
  // NOTE: I do not know what the difference is with the above
  getConsentsFromGraphQL = async () =>
    (
      await this.#httpService.post<
        {
          data: {
            customer: {
              consentInformation: Record<
                | 'electronicCustomerConsent'
                | 'electronicSolicitation'
                | 'socialMediaSolicitation'
                | 'enterpriseConsent',
                { value: boolean } | null
              >;
            };
          };
        },
        { query: string }
      >('customer/graph/graphql', {
        query: `{
        customer {
          consentInformation {
            electronicCustomerConsent {
                value
            }
            electronicSolicitation {
                value
            }
            socialMediaSolicitation {
                value
            }
            enterpriseConsent {
                value
            }
          }
        }
      }`,
      })
    ).data;
}
